import {Metric} from "@/cube"
import {measureSettings} from "@/cube/settings"
import numbro from "numbro"
import {formatISO, parse} from "date-fns"
import {date} from "quasar"
import _ from "lodash"

export const DASH = "-"
export const DATE_FORMAT = "DD/MM/YYYY"

// true for null/undefined/empty-string - used by the formatters below to
// decide when to fall back to DASH instead of formatting the value
export function isBlank(value: string | null): value is null {
  return value == null || value === ""
}

export function formatDate(value: string | null): string {
  return isBlank(value) ? DASH : date.formatDate(value, DATE_FORMAT)
}

export function formatDateTime(value: string | null): string {
  return isBlank(value) ? DASH : date.formatDate(value, "DD/MM/YYYY HH:mm")
}

export function formatText(value: string | null): string {
  return isBlank(value) ? DASH : value
}

export function formatValue(metric: Metric, value: string | number, round?: boolean): string {
  if (measureSettings[metric]?.type === "money") {
    return formatMoneyTrunc(value, round)
  } else {
    return <string>value
  }
}

export function formatMoneyTrunc(value: string | number, round: boolean | undefined): string {
  const formatString = round === undefined ? "-0,0" : "-0,0a"
  return numbro(value).formatCurrency(formatString, Math.trunc)
}

export function formatMoney(value: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(value)
}

export function formatDollars(value: number | null): string {
  return value == null ? DASH : formatMoney(value)
}

// sums any number of nullable dollar amounts (treating null/undefined as 0,
// but returning DASH if every value is null) before formatting the total
export function formatDollarsSum(...values: Array<number | null>): string {
  const total = values.reduce<number | null>((sum, v) => (v == null ? sum : (sum ?? 0) + v), null)
  return formatDollars(total)
}

export function formatPercent(value: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

// formats a plain percentage number (e.g. 5.2 -> "5.2%"), as distinct from
// formatPercent above which formats a fraction (e.g. 0.052 -> "5.20%")
export function formatPercentValue(value: number | null): string {
  return value == null ? DASH : `${value.toFixed(1)}%`
}

export function getIsoDate(value: string | null): string | null {
  return _.isEmpty(value)
    ? null
    : formatISO(parse(value!, "dd/MM/yyyy", new Date()), {
        representation: "date",
      })
}
